import "./style.scss";
import { deleteSpace, subscribeToSpace, unsubscribeFromSpace, checkSubscription, getReadOnlyDzLink } from "./gridApi";
import { Grid, Box } from "@mui/material";
import { formSkeleton, SPACE_ERROR_MESSAGES } from "./constants";
import FormSkeleton from "common/skeleton";
import { useMsal } from "@azure/msal-react";
import { getLoggedInUser, getSagemakerDomainByDisplayName } from "utils/utils";
import { extractApiErrorMessages } from "helpers";
import { SpaceFilters } from "pages/Explorer";
import ErrorModal from "components/ErrorModal";
import type { ColumnsType } from "antd/es/table";
import { useSearchParams } from "react-router-dom";
import { useFilterOptions } from "./useFilterOptions";
import { ExclamationCircleOutlined, FieldTimeOutlined, DownOutlined, CheckCircleOutlined, CloseCircleOutlined, QuestionCircleOutlined, LinkOutlined, EyeOutlined, ClockCircleOutlined, BookOutlined } from "@ant-design/icons";
import AddOrEditSpace from "components/AddOrEditSpace";
import SpaceActionMenu from "./SpaceActionMenu";
import { NormalizedSpace, PROFILE_TYPE_LABELS } from "utils/normalizeSpaces";
import React, { useState, useMemo, useEffect, useRef } from "react";
import ConfirmDeleteModal from "components/ConfirmDeleteModal";
import { getExpirationStatus } from "../../utils/expirationUtils";
import { Space_Invalidation_period, RDS_TYPE_DEFAULT } from "components/Drawer/constants";
import { CopyOutlined, SyncOutlined, DeleteOutlined, MoreOutlined, FilterOutlined, EditOutlined } from "@ant-design/icons";
import { Collapse, Input, Button, Empty, Table, Tooltip, message, Menu, Dropdown, Tag, Radio, Checkbox, Form, Row, Col, Space, Modal, Typography, Spin, Switch } from "antd";
import FilterModal from "./FilterModal";
import { fetchEditMetadata } from "components/AddOrEditSpace/AddOrEditSpaceApi";
import { IEditSpacePayload } from "components/Drawer/types";
import { grantPrivileges, fetchParametersSecrets, updateSpaceParameters } from "components/Drawer/drawerApi";
import { SettingOutlined, PlusOutlined, MinusCircleOutlined, BellOutlined, BellFilled } from "@ant-design/icons";
import { getAgCreds, IGetAgCredsResponse } from "components/ObjectList/objectListApi";
import { IGetAgCreds } from "components/ObjectList";

const { Panel } = Collapse;
const { Text } = Typography;
interface SortOptions {
  sortFields: Array<{ field: string; order: "asc" | "desc" } | string>;
}

interface PaginationState {
  current: number;
  pageSize: number;
  total: number;
  hasNext: boolean;
  hasPrevious: boolean;
}

interface SpaceFolderGridProps {
  spaces: NormalizedSpace[];
  useMySpaces: boolean;
  regionName: string;
  domainName: string;
  viewObjects: (spaceName: string,
    region: string,
    spacePrefix: string,
    bucketName: string,
    spaceDisplayName: string,
    sus_git_repo: string,
    sus_project_id: string,
    sus_project_link: string,
    sus_project_metadata: string,
    gxpFlag: string,
    data_account_id: string,
    profileType: string,
    metaDataUsers?: Array<{ user_email: string; access_level: string; }>,
    fileSystemType?: string) => void;
  onRefreshSpaces: () => void;
  isSpaceListFetching: boolean;
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  handleSearchTermChange: (term: string) => void;
  handleAutoRefreshSpaces: () => void;
  filters: SpaceFilters;
  onFiltersChange: (filters: Partial<SpaceFilters>) => void;
  sortOptions: SortOptions;
  onSortChange: (sort: Partial<SortOptions>) => void;
  pagination: PaginationState;
  onPaginationChange: (page: number, pageSize?: number) => void;
  refreshKey: number;
}

export interface SpaceRow {
  spaceName: string;
  bucketName: string;
  regionName: string;
  spaceNameMasked?: string;
  projectType: "Data" | "Analytics" | "–";
  profileType: string;
  gxpFlag: "GxP" | "Non-GxP" | "–";
  expiration: string;
  __onClick__?: (space: string, region: string, spacePrefix: string, bucketName: string, spaceDisplayName: string, sus_git_repo: string, sus_project_id: string, sus_project_link: string, sus_project_metadata: string, gxpFlag: string, data_account_id: string, profileType: string, metaDataUsers?: Array<{ user_email: string; access_level: string; }>, fileSystemType?: string) => void;
  metaDataUsers: string;
  sus_git_repo: string;
  sus_project_id: string;
  sus_project_link: string;
  sus_project_metadata: string;
  data_account_id: string;
  is_active?: number;
  create_error?: string;
  sus_project_status?: string;
  failure_reason?: string;
  last_activity?: string;
  last_notebook_activity?: string;
  last_job_run?: string;
  last_job_run_feature_eng?: string;
  is_git_repo_needed?: boolean;
  file_system_type?: string;
  byoc_app_status?: string;
  byoc_inactive_reason?: string;
  byoc_app_last_update?: string;
}

export interface IrequestBody {
  region: string;
  data_domain_name: string;
  bucket_name: string;
  space_name: string;
  reason?: string;
  harddelete?: boolean;
  data_account_id: string;
  bucket_type?: string;
  space_name_mask?: string;
  gitRepoDeletion?: boolean;
  gitRepoLink?: string;
}

// Self-contained subscribe/unsubscribe menu item.
// Owns all subscription state so parent re-renders never close the dropdown.
interface SubscribeMenuItemProps {
  record: SpaceRow;
  domainName: string;
  isOpen: boolean;
  onClose: () => void;
}

const SubscribeMenuItem: React.FC<SubscribeMenuItemProps> = ({ record, domainName, isOpen, onClose }) => {
  const msalInstance = useMsal();
  const [isChecking, setIsChecking] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const hasChecked = useRef(false);

  useEffect(() => {
    if (!isOpen || hasChecked.current) return;
    hasChecked.current = true;
    const loggedInUser = getLoggedInUser();
    setIsChecking(true);
    checkSubscription(msalInstance, record.spaceName, loggedInUser)
      .then((response) => { setIsSubscribed(response.isSubscribed); })
      .catch((err) => { console.error("Error checking subscription:", err); })
      .finally(() => { setIsChecking(false); });
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToggle = async () => {
    const loggedInUser = getLoggedInUser();
    setIsToggling(true);
    try {
      const payload: IGetAgCreds = {
        bucket_name: record.bucketName,
        region: record.regionName,
        data_account_id: record.data_account_id,
        space_name: record.spaceName,
        access_level: "READWRITE",
      };
      const credsResponse = await getAgCreds(msalInstance, payload);
      if (credsResponse === false || !credsResponse) {
        message.error("You do not have access to this project. Please contact the project owner for access.");
        return;
      }
      const creds = credsResponse as IGetAgCredsResponse;
      const userRole = creds.user_role?.toLowerCase();
      const accessLevel = creds.access_level?.toUpperCase();
      const hasAccess =
        userRole === "ro" || userRole === "rw" || userRole === "owner" || userRole === "read" ||
        accessLevel === "READ" || accessLevel === "READWRITE";
      if (!hasAccess) {
        message.error("You do not have access to this project. Please contact the project owner for access.");
        return;
      }
      const subscriptionPayload = { user_email: loggedInUser, space_name: record.spaceName, domain_name: domainName };
      if (isSubscribed) {
        const response = await unsubscribeFromSpace(msalInstance, subscriptionPayload);
        if (response.success) {
          setIsSubscribed(false);
          hasChecked.current = false;
          message.success(`Successfully unsubscribed from ${record.spaceNameMasked || record.spaceName}`);
        } else {
          message.error(response.error || "Failed to unsubscribe. Please try again.");
        }
      } else {
        const response = await subscribeToSpace(msalInstance, subscriptionPayload);
        if (response.success) {
          setIsSubscribed(true);
          hasChecked.current = false;
          message.success(`Successfully subscribed to ${record.spaceNameMasked || record.spaceName}`);
        } else {
          message.error(response.error || "Failed to subscribe. Please try again.");
        }
      }
    } catch (error: any) {
      message.error("You do not have access to this project. Please contact the project owner for access.");
    } finally {
      setIsToggling(false);
      onClose();
    }
  };

  return (
    <Menu.Item
      key="subscribeSpace"
      disabled={isChecking || isToggling}
      style={{ color: "#1677ff", fontWeight: 500, fontSize: 15, paddingLeft: 16 }}
      onClick={(e) => {
        e.domEvent.stopPropagation();
        handleToggle();
      }}
    >
      <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
        {(isChecking || isToggling) ? (
          <Spin size="small" />
        ) : isSubscribed ? (
          <BellFilled style={{ fontSize: 16, color: "#52c41a" }} />
        ) : (
          <BellOutlined style={{ fontSize: 16 }} />
        )}
        {isChecking ? "Checking..." : isToggling ? "Updating..." : isSubscribed ? "Unsubscribe" : "Subscribe space"}
      </span>
    </Menu.Item>
  );
};

const FILTER_KEY_LABELS: Record<string, string> = {
  bucket_type_filter: "Bucket Type",
  licensing_obligation_filter: "Licensing",
  region: "Region",
  study_id: "Study ID",
  project_id: "Project ID",
  group_filter: "Group",
  owner_filter: "Owner",
  projectType: "Project Type",
  profileType: "Profile Type",
  classification_filter: "Classification",
  is_active_filter: "Status",
  space_name_sort: "Sort (Space Name)",
  region_name_sort: "Sort (Region)",
};

const FILTER_VALUE_LABELS: Record<string, string> = {
  // Profile types – derived from the single canonical source
  ...Object.fromEntries(Object.entries(PROFILE_TYPE_LABELS).map(([k, v]) => [k, v])),
  // Project types
  analytics: "Analytics",
  data: "Data",
  dse_app: "DSE App",
  // Bucket types
  gxp: "GxP",
  nongxp: "Non-GxP",
  // Status
  "1": "Active",
  "0": "Inactive",
  "-1": "Provisioning",
  // Sort orders
  asc: "Ascending",
  desc: "Descending",
};

// ---------------------------------------------------------------------------
// Module-scope date formatters — constructed once per page load.
// These depend only on the browser's local timezone, which is stable for the
// lifetime of the page, so there is no need to rebuild them on every render.
// ---------------------------------------------------------------------------
const LOCAL_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
const LOCAL_BASE_OPTIONS: Intl.DateTimeFormatOptions = {
  timeZone: LOCAL_TZ,
  day: "numeric",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
};
const localFormatter24h = new Intl.DateTimeFormat("en-GB", { ...LOCAL_BASE_OPTIONS, hour12: false });
const localFormatter12h = new Intl.DateTimeFormat("en-GB", { ...LOCAL_BASE_OPTIONS, hour12: true, timeZoneName: "short" });

/**
 * Formats a date string using the browser's local timezone.
 * Returns `"–"` (for 24-hour displays) or `null` (for tooltip content) when the
 * value is absent or unparseable.
 *
 * @param value   - ISO / parseable date string
 * @param hour12  - true → include AM/PM + timezone abbreviation; false → 24-hour clock
 */
function formatLocalDate(value: string | undefined | null, hour12: boolean): string | null {
  if (!value) return hour12 ? null : "–";
  try {
    const d = new Date(value);
    if (isNaN(d.getTime())) return hour12 ? null : "–";
    const formatter = hour12 ? localFormatter12h : localFormatter24h;
    const parts = formatter.formatToParts(d);
    const get = (type: Intl.DateTimeFormatPartTypes) => parts.find(p => p.type === type)?.value ?? "";
    const base = `${get("day")} ${get("month")} ${get("year")} ${get("hour")}:${get("minute")}`;
    if (hour12) {
      const rawTz = get("timeZoneName");
      const tzName = rawTz.startsWith("GMT") ? rawTz.replace("GMT", "UTC") : rawTz;
      return `${base} ${get("dayPeriod").toUpperCase()}${tzName ? ` (${tzName})` : ""}`;
    }
    return base;
  } catch {
    return hour12 ? null : "–";
  }
}
// ---------------------------------------------------------------------------

const SpaceFolderGrid: React.FC<SpaceFolderGridProps> = ({
  spaces,
  regionName,
  domainName,
  viewObjects,
  onRefreshSpaces,
  isSpaceListFetching,
  searchTerm,
  setSearchTerm,
  handleSearchTermChange,
  filters,
  onFiltersChange,
  sortOptions,
  onSortChange,
  pagination,
  onPaginationChange,
  handleAutoRefreshSpaces,
  refreshKey,
}) => {
  const [filterResetSignal, setFilterResetSignal] = useState(0);
  const [appliedFilters, setAppliedFilters] = useState<Record<string, any>>({});
  const [isProfileTypeDropdownOpen, setIsProfileTypeDropdownOpen] = useState(false);
  const [selectedProfileTypes, setSelectedProfileTypes] = useState<string[]>([]);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isFilterModalVisible, setIsFilterModalVisible] = useState(false);
  const [hoveredRowKey, setHoveredRowKey] = useState<string | null>(null);
  const [selectedSpaceGxpFlag, setSelectedSpaceGxpFlag] = useState<string>("");
  const [isErrorModalVisible, setIsErrorModalVisible] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState<string>("");
  const [showHealthStatus, setShowHealthStatus] = useState(false);
  const [requestBody, setRequestBody] = useState<IrequestBody>({
    region: "",
    data_domain_name: "",
    bucket_name: "",
    space_name: "",
    data_account_id: "",
    space_name_mask: "",
  });

  // Parameters & Secrets Modal State
  const [parametersSecretsModalVisible, setParametersSecretsModalVisible] = useState(false);
  const [selectedSpaceForParams, setSelectedSpaceForParams] = useState<SpaceRow | null>(null);
  const [parameters, setParameters] = useState<Array<{ name: string; value: string; path?: string }>>([]);
  const [secrets, setSecrets] = useState<Array<{ name: string; value: string; arn?: string }>>([]);
  const [originalParameters, setOriginalParameters] = useState<Array<{ name: string; value: string; path?: string }>>([]);
  const [originalSecrets, setOriginalSecrets] = useState<Array<{ name: string; value: string; arn?: string }>>([]);
  const [deletedParameters, setDeletedParameters] = useState<string[]>([]);
  const [deletedSecrets, setDeletedSecrets] = useState<string[]>([]);
  const [loadingParameters, setLoadingParameters] = useState(false);
  const [isSavingParameters, setIsSavingParameters] = useState(false);

  // Derived: true when the user has made any change since the modal was last loaded
  const hasChanges = useMemo(() => {
    if (deletedParameters.length > 0 || deletedSecrets.length > 0) return true;

    const validOrigParams = originalParameters.filter(p => p.name.trim());
    const validCurrParams = parameters.filter(p => p.name.trim());
    if (validOrigParams.length !== validCurrParams.length) return true;
    const origParamMap = new Map(validOrigParams.map(p => [p.name, p.value]));
    for (const curr of validCurrParams) {
      if (origParamMap.get(curr.name) !== curr.value) return true;
    }

    const validOrigSecrets = originalSecrets.filter(s => s.name.trim());
    const validCurrSecrets = secrets.filter(s => s.name.trim());
    if (validOrigSecrets.length !== validCurrSecrets.length) return true;
    const origSecretMap = new Map(validOrigSecrets.map(s => [s.name, s.value]));
    for (const curr of validCurrSecrets) {
      if (origSecretMap.get(curr.name) !== curr.value) return true;
    }

    return false;
  }, [parameters, secrets, originalParameters, originalSecrets, deletedParameters, deletedSecrets]);

  // Controlled dropdown
  const [openDropdownKey, setOpenDropdownKey] = useState<string | null>(null);
  // Which record's edit metadata drawer is open (rendered outside dropdown)
  const [editingRecord, setEditingRecord] = useState<SpaceRow | null>(null);

  const msalInstance = useMsal();
  const [URLParams, setURLParams] = useSearchParams();
  const [activeKeys, setActiveKeys] = useState<string[]>(["1"]);

  const collapseRef = useRef<HTMLDivElement>(null);

  const {
    availableProjectTypes,
    availableProfileTypes,
    availableBucketTypes,
    isLoadingFilterOptions,
    fetchFilterOptions,
  } = useFilterOptions(msalInstance);

  useEffect(() => {
    if (domainName) {
      fetchFilterOptions(domainName, regionName !== "All" ? regionName : undefined);
    }
  }, [domainName, regionName, fetchFilterOptions]);

  useEffect(() => {
    if (refreshKey > 0) {

      setAppliedFilters({});
      setFilterResetSignal(prev => prev + 1);
    }
  }, [refreshKey]);

  // Build SpaceRow[] from spaces - spaces are already filtered/sorted by API
  const allRows: SpaceRow[] = spaces.map((space) => {

    if (typeof space === "string") {
      return {
        spaceName: space,
        spaceNameMasked: undefined,
        bucketName: "-",
        regionName: "–",
        projectType: "–",
        profileType: "–",
        gxpFlag: "–",
        expiration: "–",
        metaDataUsers: "",
        sus_git_repo: "",
        sus_project_id: "",
        sus_project_link: "",
        sus_project_metadata: "",
        data_account_id: "",
        is_active: 1,
        create_error: "",
        project_status: "",
        failure_reason: "",
        last_activity: "",
        last_notebook_activity: "",
        last_job_run: "",
        last_job_run_feature_eng: "",
      };
    } else {
      // Check all possible locations for these fields
      const createError =
        space.create_error ||
        (space as any).create_error ||
        (space as any).metadata?.create_error ||
        "";

      const isActive =
        space.is_active !== undefined ? space.is_active :
          (space as any).is_active !== undefined ? (space as any).is_active :
            (space as any).metadata?.is_active !== undefined ? (space as any).metadata?.is_active :
              1; // default to active

      const spaceNameMasked =
        (space as any).spaceNameMasked ||
        (space as any).space_name_mask ||
        (space as any).metadata?.space_name_mask ||
        space.spaceNameMasked ||
        undefined;

      return {
        spaceNameMasked: spaceNameMasked,
        spaceName: space.spaceName || (space as any).name || "–",
        bucketName: space.bucketName || (space as any).name || "–",
        regionName: space.regionName || "–",
        projectType: (space as any).projectType || "–",
        profileType: (space as any).profileType || "–",
        gxpFlag: (space as any).gxpFlag || "–",
        expiration: (space as any).expiration || "–",
        metaDataUsers: space.metaDataUsers,
        sus_git_repo: space?.sus_git_repo || "",
        sus_project_id: space?.sus_project_id || "",
        sus_project_link: space?.sus_project_link || "",
        sus_project_metadata: space?.sus_project_metadata || "",
        data_account_id: space?.data_account_id || "",
        is_active: isActive,
        create_error: createError,
        sus_project_status: space?.sus_project_status || (space as any).sus_project_status || "",
        failure_reason: space?.failure_reason || (space as any).failure_reason || "",
        last_activity: space?.last_activity || (space as any).last_activity || "",
        last_notebook_activity: space?.last_notebook_activity || (space as any).last_notebook_activity || "",
        last_job_run: space?.last_job_run || (space as any).last_job_run || "",
        last_job_run_feature_eng: space?.last_job_run_feature_eng || (space as any).last_job_run_feature_eng || "",
        file_system_type: space?.file_system_type || "",
        byoc_app_status: (space.byoc_app_status || "").toLowerCase(),
        byoc_inactive_reason: space.byoc_inactive_reason || "",
        byoc_app_last_update: space.byoc_app_last_update || "",
      };
    }
  });

  // Auto refresh interval
  useEffect(() => {
    const interval = setInterval(() => {
      const drawerOpen = !!document.querySelector(".ant-drawer-open");

      if (!drawerOpen) {
        handleAutoRefreshSpaces();
      }
    }, Space_Invalidation_period);

    return () => clearInterval(interval);
  }, [handleAutoRefreshSpaces]);


  const hasCreateError = (r: SpaceRow) => {
    const hasError = typeof r.create_error === "string" && r.create_error.trim() !== "";
    return hasError;
  };

  const formatLastActivity = (lastActivity?: string): string =>
    formatLocalDate(lastActivity, false) ?? "–";

  const getHealthStatus = (record: SpaceRow) => {
    // Determine health status based on sus_project_status
    const projectStatus = record.sus_project_status?.toLowerCase();

    // Only show status if sus_project_status is available
    if (projectStatus) {
      if (projectStatus === "broken" || projectStatus === "failed") {
        return "Broken";
      } else if (projectStatus === "working" || projectStatus === "active" || projectStatus === "success" || projectStatus === "completed") {
        return "Active";
      }
    }

    // Default to Unknown if sus_project_status is not available or doesn't match known statuses
    return "Unknown";
  };

  const getHealthStatusColor = (status: string) => {
    switch (status) {
      case "Active":
        return "#52c41a"; // Green
      case "Broken":
        return "#ff4d4f"; // Red
      case "Unknown":
      default:
        return "#faad14"; // Orange
    }
  };

  const getHealthStatusIcon = (status: string) => {
    switch (status) {
      case "Active":
        return CheckCircleOutlined;
      case "Broken":
        return CloseCircleOutlined;
      case "Unknown":
      default:
        return QuestionCircleOutlined;
    }
  };

 const cleanErrorText = (v?: string): string => {
  if (!v || typeof v !== "string" || v.trim() === "") return "";

  const trimmed = v.trim().replace(/^['"]+|['"]+$/g, "");
  if (trimmed === "") return "";

  try {
    // Parse into unknown, then narrow
    const parsed: unknown = JSON.parse(trimmed);

    if (
      parsed &&
      typeof parsed === "object" &&
      "message" in parsed &&
      typeof (parsed as { message?: unknown }).message !== "undefined"
    ) {
      const msg = (parsed as { message?: unknown }).message;
      return typeof msg === "string" ? msg : JSON.stringify(msg);
    }
  } catch {
    // Not JSON – fall through and return trimmed string
  }

  return trimmed;
};

  const showDeleteModal = () => setIsDeleteModalVisible(true);

  const handleDeleteAccess = (metaDataUsers: string) => {
    const loggedInUser = getLoggedInUser();
    const users = JSON.parse(metaDataUsers);

    if (users.find((value: { user_email: string }) => value.user_email == loggedInUser)) {
      showDeleteModal();
    } else {
      message.info("You don't have sufficient privileges to delete this Space");
    }
  };

  // Track which dropdown is open (controlled) — no API calls here
  const handleMenuOpen = (record: SpaceRow, open: boolean) => {
    const spaceKey = `${record.bucketName}-${record.spaceName}`;
    setOpenDropdownKey(open ? spaceKey : null);
  };

  const handleConfirm = async (hardDelete: boolean, reason: { value: string, label: string }, delRepo: boolean) => {
    setIsDeleteModalVisible(false);

    const isNonGxpSpace = selectedSpaceGxpFlag?.toLowerCase() === "nongxp" || selectedSpaceGxpFlag?.toLowerCase() === "non-gxp";
    const bucketType = isNonGxpSpace ? "nongxp" : "gxp";
    const payload = isNonGxpSpace
      ? { ...requestBody, reason: "", bucket_type: bucketType, harddelete: false, gitRepoDeletion: delRepo }
      : { ...requestBody, reason: reason?.value, harddelete: hardDelete, bucket_type: bucketType, gitRepoDeletion: delRepo };

    const response = await deleteSpace(msalInstance, payload);

    if (response.success) {
      const successMessage = isNonGxpSpace
        ? `Deletion of space ${payload.space_name_mask || payload.space_name} has been queued. You will be notified via email once it is completed`
        : `${hardDelete ? "Permanent deletion" : "Deletion"} of space ${payload.space_name_mask || payload.space_name} with reason ${reason.label} has been queued. You will be notified via email once it is completed`;
      message.success(successMessage);
    } else {
      const errorMessage = response.error || `Error deleting the space ${payload.space_name_mask || payload.space_name}.`;
      if (errorMessage.includes("Action restricted: Non-GxP Space containing active files cannot be deleted") ||
        errorMessage.includes("Action restricted: GxP Space containing active files or files with delete marker cannot be deleted")) {
        setErrorModalMessage(errorMessage);
        setIsErrorModalVisible(true);
      } else {
        const displayMessage = `Error while deleting the Space. ${errorMessage}`;
        message.error(displayMessage);
      }
    }
  };

  type DomainInfo = {
    display_name: string;
    aws_account_id?: string | null;
  };

  // helper to map domain -> account id (same logic you use in Drawer)
  function getAwsAccountId(displayName: string): string | null {
    const json = localStorage.getItem("domainsData") || "[]";
    const domainsData = JSON.parse(json) as DomainInfo[]; // typed!

    const domain = domainsData.find((d: DomainInfo) => d.display_name === displayName);
    return domain?.aws_account_id ?? null;
  }

  // inside SpaceFolderGrid component
  const handleRetry = async (record: SpaceRow) => {
    try {
      message.loading({ content: "Retrying space creation...", key: "retry" });

      // Failed creation is replayed from DynamoDB. The UI sends only the space name.
      if (record.is_active === -1) {
        const response = await grantPrivileges(
          msalInstance,
          { space_name: record.spaceName },
          "create-retry",
        );

        if (response && response.status === true) {
          const successMessage = response.message || "The retry for space creation or update has been initiated successfully.";
          message.success({ content: successMessage, key: "retry", duration: 6 });
          onRefreshSpaces();
        } else {
          const errorMessage = (response && response.message) || "Failed to retry space creation. Please try again later.";
          message.error({ content: errorMessage, key: "retry", duration: 5 });
        }
        return;
      }

      // Failed update still sends the editable fields the user last saved.
      const meta = await fetchEditMetadata(
        msalInstance,
        record.spaceName,
        record.spaceName,
        record.regionName,
        domainName,
      );

      if (!meta || typeof meta === "string") {
        throw new Error("Failed to fetch metadata");
      }

      const apiMode = "edit";

      const loggedInUser = getLoggedInUser() || meta.requested_for || "";
      const retryFileSystem =
        meta.file_system_type ??
        (meta as any).file_system ??
        (record as any).file_system_type ??
        "";

      const primary = meta["primary-data-domain"] || domainName || "";
      const resolvedAccountId =
        (meta as any).data_account_id ||
        record.data_account_id ||
        (primary ? getAwsAccountId(primary) : null) ||
        null;

      // Edit-retry: use IEditSpacePayload with lean metadata
      const editRetryPayload: IEditSpacePayload = {
        space_name: record.spaceName,
        file_system: retryFileSystem,
        metadata: {
          name: record.spaceName,
          mapped_groups: JSON.stringify(meta.mapped_groups || []),
          classification: meta.classification || "",
          secondary_data_domain: meta.secondary_data_domain || "",
          mapped_users: JSON.stringify(meta.mapped_users || []),
          licensing_obligation: meta.licensing_obligation || "",
          datasource: meta.datasource || "",
          space_description: meta.space_description || "",
          retention_period_type: meta.retention_period_type || "",
          retention_period_value: meta.retention_period_value || "",
          need_cloud_gpu_compute: (meta as any).need_cloud_gpu_compute || "",
          need_rds_storage: (meta as any).need_rds_storage || "",
          rds_storage_type: (meta as any).rds_storage_type || ((meta as any).need_rds_storage === "yes" ? RDS_TYPE_DEFAULT : ""),
        },
        mode: "Edit",
        data_account_id: resolvedAccountId,
        loggedInUserEmail: loggedInUser,
        is_git_repo_needed: record.is_git_repo_needed || true,
      };

      // Conditionally add rnd_project_id or rnd_study_id
      if (meta.rnd_project_id) {
        editRetryPayload.metadata.rnd_project_id = meta.rnd_project_id;
      } else if (meta.rnd_study_id) {
        editRetryPayload.metadata.rnd_study_id = meta.rnd_study_id;
      }

      const response = await grantPrivileges(msalInstance, editRetryPayload, apiMode);

      // Check response status from API
      if (response && response.status === true) {
        const successMessage = response.message || "The retry for space creation or update has been initiated successfully.";
        message.success({ content: successMessage, key: "retry", duration: 6 });
        onRefreshSpaces();
      } else {
        const errorMessage = (response && response.message) || "Failed to retry space creation. Please try again later.";
        message.error({ content: errorMessage, key: "retry", duration: 5 });
      }
    } catch (e: any) {

      // Check if it's a 403 error
      const status = e.response?.status || e.status;
      const appException = e.response?.data?.app_exception;
      const errorMessage = e.response?.data?.context?.message || e.message;

      if (status === 403) {
        if (appException === "EditSpaceForbiddenForNonOwners") {
          message.error({
            content: "You don't have permission to retry space creation or updation. Only space owners can perform this action.",
            key: "retry",
            duration: 5,
          });
        } else {
          message.error({
            content: errorMessage || "Access denied. You don't have permission to retry this operation.",
            key: "retry",
            duration: 5,
          });
        }
      } else {
        message.error({
          content: "Failed to trigger retry. Please try again later.",
          key: "retry",
        });
      }
    }
  };

  const handleCancel = () => {
    message.info("Deletion cancelled");
    setIsDeleteModalVisible(false);
    setRequestBody({
      region: "",
      data_domain_name: "",
      bucket_name: "",
      space_name: "",
      data_account_id: "",
    });
  };

  // Parameters & Secrets handler
  const handleOpenParametersSecretsModal = async (record: SpaceRow) => {
    // Check if user is the owner using the same logic as delete access
    const loggedInUser = getLoggedInUser();

    try {
      const users = JSON.parse(record.metaDataUsers || "[]");
      const userHasAccess = users.find((value: { user_email: string }) => value.user_email === loggedInUser);

      if (!userHasAccess) {
        message.error("Only the space owner can view or edit parameters and secrets");
        return;
      }
    } catch (error) {
      message.error("Unable to verify ownership permissions");
      return;
    }

    setSelectedSpaceForParams(record);
    setParametersSecretsModalVisible(true);
    setLoadingParameters(true);

    try {
      const data = await fetchParametersSecrets(
        msalInstance,
        record.spaceName,
        domainName,
      );

      if (data) {
        const decryptedParameters = data.parameters.map(param => ({
          name: param.name,
          value: param.value,
          path: param.path,
        }));

        const decryptedSecrets = data.secrets.map(secret => ({
          name: secret.name,
          value: secret.value,
          arn: secret.arn,
        }));

        const paramData = decryptedParameters.length > 0 ? decryptedParameters : [{ name: "", value: "" }];
        const secretData = decryptedSecrets.length > 0 ? decryptedSecrets : [{ name: "", value: "" }];

        setParameters(paramData);
        setSecrets(secretData);
        // Store original values for comparison
        setOriginalParameters(JSON.parse(JSON.stringify(paramData)));
        setOriginalSecrets(JSON.parse(JSON.stringify(secretData)));
      } else {
        setParameters([{ name: "", value: "" }]);
        setSecrets([{ name: "", value: "" }]);
        setOriginalParameters([{ name: "", value: "" }]);
        setOriginalSecrets([{ name: "", value: "" }]);
      }
    } catch (error) {
      console.error("Error fetching parameters:", error);
      message.error("Failed to load parameters and secrets");
      setParameters([{ name: "", value: "" }]);
      setSecrets([{ name: "", value: "" }]);
      setOriginalParameters([{ name: "", value: "" }]);
      setOriginalSecrets([{ name: "", value: "" }]);
    } finally {
      setLoadingParameters(false);
    }
  };

  // Helper function to compare parameters/secrets and generate edited_params
  const getEditedParams = () => {
    const edittedParams: Array<{ field: string; name: string }> = [];

    // Compare parameters
    const validOriginalParams = originalParameters.filter(p => p.name.trim() && p.value.trim());
    const validCurrentParams = parameters.filter(p => p.name.trim() && p.value.trim());

    // Check for modified/deleted parameters
    validOriginalParams.forEach(origParam => {
      const currentParam = validCurrentParams.find(p => p.name === origParam.name);
      if (!currentParam) {
        // Parameter was deleted - only send parameter name
        edittedParams.push({
          field: "parameter_name",
          name: origParam.name,
        });
      } else if (currentParam.value !== origParam.value) {
        // Parameter was modified - only send parameter name
        edittedParams.push({
          field: "parameter_name",
          name: origParam.name,
        });
      }
    });

    // Check for added parameters
    validCurrentParams.forEach(currentParam => {
      const origParam = validOriginalParams.find(p => p.name === currentParam.name);
      if (!origParam) {
        // Parameter was added - only send parameter name
        edittedParams.push({
          field: "parameter_name",
          name: currentParam.name,
        });
      }
    });

    // Compare secrets
    const validOriginalSecrets = originalSecrets.filter(s => s.name.trim() && s.value.trim());
    const validCurrentSecrets = secrets.filter(s => s.name.trim() && s.value.trim());

    // Check for modified/deleted secrets
    validOriginalSecrets.forEach(origSecret => {
      const currentSecret = validCurrentSecrets.find(s => s.name === origSecret.name);
      if (!currentSecret) {
        // Secret was deleted - only send secret name
        edittedParams.push({
          field: "secret_name",
          name: origSecret.name,
        });
      } else if (currentSecret.value !== origSecret.value) {
        // Secret was modified - only send secret name
        edittedParams.push({
          field: "secret_name",
          name: origSecret.name,
        });
      }
    });

    // Check for added secrets
    validCurrentSecrets.forEach(currentSecret => {
      const origSecret = validOriginalSecrets.find(s => s.name === currentSecret.name);
      if (!origSecret) {
        // Secret was added - only send secret name
        edittedParams.push({
          field: "secret_name",
          name: currentSecret.name,
        });
      }
    });

    return edittedParams;
  };

  const handleSaveParametersSecrets = async () => {
    if (!selectedSpaceForParams) return;

    const validParams = parameters.filter(p => p.name.trim() && p.value.trim());
    const validSecrets = secrets.filter(s => s.name.trim() && s.value.trim());

    const partialParam = parameters.find(p =>
      (p.name.trim() && !p.value.trim()) || (!p.name.trim() && p.value.trim()),
    );
    const partialSecret = secrets.find(s =>
      (s.name.trim() && !s.value.trim()) || (!s.name.trim() && s.value.trim()),
    );

    if (partialParam || partialSecret) {
      message.error("Please fill both name and value for all entries, or leave both empty");
      return;
    }

    if (
      validParams.length === 0 &&
      validSecrets.length === 0 &&
      deletedParameters.length === 0 &&
      deletedSecrets.length === 0
    ) {
      message.error("Please add at least one parameter and one secret");
      return;
    }

    if (validParams.length === 0 && validSecrets.length > 0) {
      message.error("Please add at least one parameter when adding secrets");
      return;
    }

    if (validParams.length > 0 && validSecrets.length === 0) {
      message.error("Please add at least one secret when adding parameters");
      return;
    }

    const edittedParams = getEditedParams();

    setLoadingParameters(true);
    setIsSavingParameters(true);
    try {
      await updateSpaceParameters(
        msalInstance,
        selectedSpaceForParams.spaceName,
        validParams,
        validSecrets,
        deletedParameters,
        deletedSecrets,
        edittedParams.length > 0 ? edittedParams : undefined,
      );
      message.success("Parameters and secrets updated successfully");
      setDeletedParameters([]);
      setDeletedSecrets([]);
      onRefreshSpaces();

      // Re-fetch updated data and refresh the modal
      try {
        const data = await fetchParametersSecrets(
          msalInstance,
          selectedSpaceForParams.spaceName,
          domainName,
        );
        if (data) {
          const paramData = data.parameters.length > 0
            ? data.parameters.map(p => ({ name: p.name, value: p.value, path: p.path }))
            : [{ name: "", value: "" }];
          const secretData = data.secrets.length > 0
            ? data.secrets.map(s => ({ name: s.name, value: s.value, arn: s.arn }))
            : [{ name: "", value: "" }];
          setParameters(paramData);
          setSecrets(secretData);
          setOriginalParameters(JSON.parse(JSON.stringify(paramData)));
          setOriginalSecrets(JSON.parse(JSON.stringify(secretData)));
        }
      } catch (fetchErr) {
        console.error("Failed to re-fetch parameters after save:", fetchErr);
      }
    } catch (error) {
      console.error("Failed to update parameters/secrets:", error);
      extractApiErrorMessages(error, "Failed to update parameters and secrets").forEach(msg => message.error(msg));
    } finally {
      setLoadingParameters(false);
      setIsSavingParameters(false);
    }
  };

  const handleFilterModalOpen = () => {
    setIsFilterModalVisible(true);
  };

const handleFilterApply = (filters: Record<string, any>) => {
  setAppliedFilters(filters);
  const mapped: Partial<SpaceFilters> = {
    bucketTypeFilter: filters.bucket_type_filter ? [filters.bucket_type_filter] : [],
    licensingFilter: filters.licensing_obligation_filter || undefined,
    selectedRegions: Array.isArray(filters.region) ? filters.region : [],
    studyIdFilter: filters.study_id,
    projectIdFilter: filters.project_id,
    groupFilter: filters.group_filter,
    ownerFilter: filters.owner_filter,
    projectTypeFilter: filters.projectType || [],
    profileTypeFilter: filters.profileType || [],
    classificationFilter: filters.classification_filter || [],
    isActiveFilter: filters.is_active_filter || [],
  };
  onFiltersChange(mapped);

  const sortPayload: { field: string; order: "asc" | "desc" }[] = [];
  if (filters.space_name_sort) sortPayload.push({ field: "space_name", order: filters.space_name_sort });
  if (filters.region_name_sort) sortPayload.push({ field: "region_name", order: filters.region_name_sort });
  onSortChange({ sortFields: sortPayload });

  setIsFilterModalVisible(false);
};

  const handleTableChange = (pag: any, filters: any, sorter: any) => {
    const newAppliedFilters: Record<string, any> = { ...appliedFilters };

    if (sorter && sorter.field && sorter.order !== undefined) {
      const sortField = sorter.field;
      const sortOrder = sorter.order === "ascend" ? "asc" : "desc";

      // Map column keys to API field names
      const fieldMapping: { [key: string]: string } = {
        "spaceName": "space_name",
        "regionName": "region_name",
        "expiration": "expires_on",
      };

      const apiFieldName = fieldMapping[sortField] || sortField;

      onSortChange({
        sortFields: [{ field: apiFieldName, order: sortOrder }],
      });

      // Update appliedFilters with sort information
      // Clear all sort fields first
      delete newAppliedFilters.space_name_sort;
      delete newAppliedFilters.region_name_sort;
      delete newAppliedFilters.expires_on_sort;

      // Add the new sort field
      newAppliedFilters[`${apiFieldName}_sort`] = sortOrder;
      setAppliedFilters(newAppliedFilters);
    } else if (sorter && sorter.order === undefined) {
      // Clear sorting when user cancels sort (third click)
      onSortChange({ sortFields: [] });

      // Remove all sort fields from appliedFilters
      delete newAppliedFilters.space_name_sort;
      delete newAppliedFilters.region_name_sort;
      delete newAppliedFilters.expires_on_sort;
      setAppliedFilters(newAppliedFilters);
    }

    setTimeout(() => {
      const apiFilters: Partial<SpaceFilters> = {};
      const filterUpdates: Record<string, any> = { ...newAppliedFilters };

      if (filters.projectType?.length) {
        const filtered = filters.projectType.filter((x: string) => x !== "–" && x !== "");
        apiFilters.projectTypeFilter = filtered;
        filterUpdates.projectType = filtered;
      } else {
        apiFilters.projectTypeFilter = [];
        delete filterUpdates.projectType;
      }

      if (filters.profileType?.length) {
        const filtered = filters.profileType.filter((x: string) => x !== "–" && x !== "");
        apiFilters.profileTypeFilter = filtered;
        filterUpdates.profileType = filtered;
      } else {
        apiFilters.profileTypeFilter = [];
        delete filterUpdates.profileType;
      }

      if (filters.gxpFlag?.length) {
        const filtered = filters.gxpFlag.filter((x: string) => x !== "–" && x !== "");
        apiFilters.bucketTypeFilter = filtered;
        filterUpdates.bucket_type_filter = filtered.length === 1 ? filtered[0] : undefined;
      } else {
        apiFilters.bucketTypeFilter = [];
        delete filterUpdates.bucket_type_filter;
      }

      // Preserve other filters from appliedFilters (like project_id, study_id, etc.)
      // and map them to apiFilters
      if (filterUpdates.project_id) apiFilters.projectIdFilter = filterUpdates.project_id;
      if (filterUpdates.study_id) apiFilters.studyIdFilter = filterUpdates.study_id;
      if (filterUpdates.group_filter) apiFilters.groupFilter = filterUpdates.group_filter;
      if (filterUpdates.owner_filter) apiFilters.ownerFilter = filterUpdates.owner_filter;
      if (filterUpdates.licensing_obligation_filter) apiFilters.licensingFilter = filterUpdates.licensing_obligation_filter;
      if (filterUpdates.region) apiFilters.selectedRegions = Array.isArray(filterUpdates.region) ? filterUpdates.region : [];
      if (filterUpdates.classification_filter) apiFilters.classificationFilter = filterUpdates.classification_filter;
      if (filterUpdates.is_active_filter) apiFilters.isActiveFilter = filterUpdates.is_active_filter;

      // Update appliedFilters state to sync with URL params
      setAppliedFilters(filterUpdates);
      onFiltersChange(apiFilters);
    }, 0);
  };

  // Handle sort by name button (for API)
  const handleSortByName = () => {
    const currentSort = sortOptions.sortFields[0];
    const currentOrder = typeof currentSort === "object" ? currentSort.order : "asc";
    const newOrder = currentOrder === "asc" ? "desc" : "asc";

    onSortChange({
      sortFields: [{ field: "space_name", order: newOrder }],
    });
  };

const handleRefresh = () => {
    setSearchTerm("");
    setAppliedFilters({});
    onFiltersChange({
      searchSpaceName: "",
      projectTypeFilter: [],
      bucketTypeFilter: [],
    });
    setFilterResetSignal(s => s + 1);
    onSortChange({ sortFields: [] });
    URLParams.delete("space_name");
    setURLParams(URLParams);
    onRefreshSpaces();
  };

  // Get current sort order for display
  const getCurrentSortOrder = () => {
    const currentSort = sortOptions.sortFields[0];
    if (typeof currentSort === "object") {
      return currentSort.order === "asc" ? "asc" : "desc";
    }
    return "asc";
  };

  const getSpaceErrorMessage = (record: SpaceRow) => {
    if (!record.create_error?.trim()) return "";

    return record.is_active === -1
      ? SPACE_ERROR_MESSAGES.CREATE_ERROR
      : SPACE_ERROR_MESSAGES.UPDATE_ERROR;
  };

  const columns: ColumnsType<SpaceRow> = [
    {
      title: "Space Name",
      dataIndex: "spaceName",
      key: "spaceName",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: (() => {
        const currentSort = sortOptions.sortFields[0];
        if (typeof currentSort === "object" && currentSort.field === "space_name") {
          return currentSort.order === "asc" ? "ascend" : "descend";
        }
        return null;
      })(),
      render: (text, record) => {

        const displayName = record.spaceNameMasked || record.spaceName;

        const isError = hasCreateError(record);
        const isInProgress = record.is_active === -1;


        // Only disable when IN PROGRESS without error
        if (isInProgress && !isError) {
          return (
            <Tooltip title={displayName}>
              <span className="space-name-disabled">
                {displayName}
              </span>
            </Tooltip>
          );
        }

        return (
          <span className="space-name-wrapper">
            <Tooltip title={displayName}>
              <span
                className="space-name-text"
                onClick={(e) => {
                  // Check if user is selecting text - if so, don't navigate
                  const selection = window.getSelection();
                  if (selection && selection.toString().length > 0) {
                    e.stopPropagation();
                    return;
                  }
                  // Navigate to space details
                  record.__onClick__?.(
                    record.spaceName,
                    record.regionName,
                    "",
                    record.bucketName,
                    record.spaceNameMasked || record.spaceName,
                    record.sus_git_repo,
                    record?.sus_project_id,
                    record?.sus_project_link,
                    record?.sus_project_metadata,
                    record.gxpFlag,
                    record.data_account_id,
                    record.profileType,
                    record.metaDataUsers ? JSON.parse(record.metaDataUsers) : [],
                    record.file_system_type,
                  );
                }}
              >
                {displayName}
              </span>
            </Tooltip>
            
            <Tooltip title="Copy space name">
              <CopyOutlined
                className="space-name-copy-icon"
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    await navigator.clipboard.writeText(displayName);
                    message.success("Copied space name!");
                  } catch {
                    message.error("Failed to copy space name");
                  }
                }}
              />
            </Tooltip>

            {isError && (
              <Tooltip title={getSpaceErrorMessage(record)} overlayInnerStyle={{ maxWidth: 300 }}>
                <ExclamationCircleOutlined className="space-name-error-icon" />
              </Tooltip>
            )}

            {showHealthStatus && (record.byoc_app_status || record.profileType === "App Deployment (BYOC)") && (() => {
              const isActive = record.byoc_app_status === "active";
              const ariaLabel = isActive
                ? "BYOC app status: active"
                : `BYOC app status: inactive${record.byoc_inactive_reason ? ` – ${record.byoc_inactive_reason}` : ""}`;
              const statusLabel = isActive ? "App is active" : record.byoc_inactive_reason || "App is inactive";
              const lastUpdateFormatted = formatLocalDate(record.byoc_app_last_update, true);
              const tooltipContent = lastUpdateFormatted ? (
                <div>
                  <div>{statusLabel}</div>
                  <div>Last status updated: {lastUpdateFormatted}</div>
                </div>
              ) : statusLabel;
              return (
                <Tooltip title={tooltipContent}>
                  <span
                    role="img"
                    aria-label={ariaLabel}
                    onClick={(e) => e.stopPropagation()}
                    className={`byoc-status-dot ${isActive ? "byoc-status-dot--active" : "byoc-status-dot--inactive"}`}
                  />
                </Tooltip>
              );
            })()}
          </span>
        );
      },
    },
    {
      title: "Region",
      dataIndex: "regionName",
      key: "regionName",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: (() => {
        const currentSort = sortOptions.sortFields[0];
        if (typeof currentSort === "object" && currentSort.field === "region_name") {
          return currentSort.order === "asc" ? "ascend" : "descend";
        }
        return null;
      })(),
    },
    {
      title: "Project Type",
      dataIndex: "projectType",
      key: "projectType",
      filters: availableProjectTypes,
      filteredValue: filters.projectTypeFilter,
      onFilter: () => true, // API handles filtering
    },
    {
      title: "Profile Type",
      dataIndex: "profileType",
      key: "profileType",
      filters: availableProfileTypes,
      filteredValue: filters.profileTypeFilter, // Map to appropriate API filter if available
      onFilter: () => true,
    },
    {
      title: "GxP / Non-GxP",
      dataIndex: "gxpFlag",
      key: "gxpFlag",
      filters: availableBucketTypes,
      filteredValue: filters.bucketTypeFilter,
      onFilter: () => true, // API handles filtering
      render: (_: any, record: SpaceRow) => (
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {record.gxpFlag.toLocaleLowerCase() === "nongxp" ? (
            <Tag color="green">{record.gxpFlag}</Tag>
          ) : (
            <Tag color="blue">{record.gxpFlag}</Tag>
          )}
        </div>
      ),
    },
    {
      title: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          Expires On
        </span>
      ),
      dataIndex: "expiration",
      key: "expiration",
      sorter: true,
      sortDirections: ["ascend", "descend"],
      sortOrder: (() => {
        const currentSort = sortOptions.sortFields[0];
        if (typeof currentSort === "object" && currentSort.field === "expires_on") {
          return currentSort.order === "asc" ? "ascend" : "descend";
        }
        return null;
      })(),
      render: (value: string) => {
        const status = getExpirationStatus(value);
        const color = status === "expired" ? "#ff4d4f" : status === "soon" ? "#faad14" : undefined;
        if (!color) {
          return <span>{value}</span>;
        }
        return (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
            {value}
            <Tooltip
              color="#1f1f1f"
              title={
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ display: "inline-block", width: 10, height: 10, background: "#ff4d4f", borderRadius: 2 }} />
                    <span>Expired</span>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                    <span style={{ display: "inline-block", width: 10, height: 10, background: "#faad14", borderRadius: 2 }} />
                    <span>Expiring soon (≤ 5 days)</span>
                  </div>
                </div>
              }
            >
              <FieldTimeOutlined style={{ color }} />
            </Tooltip>
          </span>
        );
      },
    },
    ...(showHealthStatus ? [{
      title: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
          SUS Health Status
        </span>
      ),
      dataIndex: "health_status",
      key: "health_status",
      width: 250,
      render: (_: any, record: SpaceRow) => {
        const status = getHealthStatus(record);
        const color = getHealthStatusColor(status);
        const IconComponent = getHealthStatusIcon(status);

        const tooltipContent = () => {
          if (status === "Broken" && record.failure_reason) {
            return record.failure_reason;
          } else if (status === "Broken" && record.create_error) {
            return record.create_error;
          } else if (status === "Unknown") {
            return "project may have been removed or is not accessible";
          } else {
            return `Space is ${status.toLowerCase()}`;
          }
        };

        const formattedLastActivity = formatLastActivity(record.last_activity);
        const formattedLastNotebookActivity = formatLastActivity(record.last_notebook_activity);
        const formattedLastJobRun = formatLastActivity(record.last_job_run);
        const formattedLastJobRunFeatureEng = formatLastActivity(record.last_job_run_feature_eng);

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <Tooltip title={tooltipContent()}>
              <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color }}>
                <IconComponent />
                {status}
              </span>
            </Tooltip>
              <span style={{ fontSize: 12, color: "#8c8c8c", display: "flex", alignItems: "flex-start", gap: 4, fontWeight: 600 }}>
                <ClockCircleOutlined style={{ fontSize: 13 }} />
                <span>Last Login: {formattedLastActivity}</span>
              </span>
            {record.last_notebook_activity && formattedLastNotebookActivity !== "–" && (
              <span style={{ fontSize: 12, color: "#8c8c8c", display: "flex", alignItems: "flex-start", gap: 4, fontWeight: 600 }}>
                <BookOutlined style={{ fontSize: 13 }} />
                <span>Last Notebook activity: {formattedLastNotebookActivity}</span>
              </span>
            )}
            {record.last_job_run && formattedLastJobRun !== "–" && (
              <span style={{ fontSize: 12, color: "#8c8c8c", display: "flex", alignItems: "flex-start", gap: 4, fontWeight: 600 }}>
                <ClockCircleOutlined style={{ fontSize: 13 }} />
                <span>Last Job Run Model Forge: {formattedLastJobRun}</span>
              </span>
            )}
            {record.last_job_run_feature_eng && formattedLastJobRunFeatureEng !== "–" && (
              <span style={{ fontSize: 12, color: "#8c8c8c", display: "flex", alignItems: "flex-start", gap: 4, fontWeight: 600 }}>
                <ClockCircleOutlined style={{ fontSize: 13 }} />
                <span>Last Job Run Feature Engg: {formattedLastJobRunFeatureEng}</span>
              </span>
            )}
          </div>
        );
      },
    }] : []),
    {
      title: "Actions",
      key: "actions",
      render: (_: any, record: SpaceRow) => {
        const hasError = hasCreateError(record);
        const isInProgress = record.is_active === -1;

        // If in-progress WITHOUT error, show em dash (no actions)
        if (isInProgress && !hasError) {
          return <span style={{ color: "#bfbfbf", cursor: "not-allowed", pointerEvents: "none" }}>—</span>;
        }

        // If in-progress WITH error, show dropdown with retry and delete options
        if (isInProgress && hasError) {
          return (
            <Dropdown
              overlay={
                <Menu className="action-menu-custom">
                  <Menu.Item
                    key="retrySpace"
                    style={{ color: "#fa541c", fontWeight: 600, fontSize: 15, paddingLeft: 16 }}
                    onClick={(e) => {
                      e.domEvent.stopPropagation();
                      setOpenDropdownKey(null);
                      handleRetry(record);
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <SyncOutlined style={{ fontSize: 16 }} />
                      Retry
                    </span>
                  </Menu.Item>

                  <Menu.Item
                    key="deleteSpace"
                    style={{ color: "#ff4d4f", fontWeight: 500, fontSize: 15, paddingLeft: 16 }}
                    onClick={() => {
                      setOpenDropdownKey(null);
                      const body: IrequestBody = {
                        region: record.regionName,
                        data_domain_name: domainName,
                        bucket_name: record.bucketName,
                        space_name: record.spaceName,
                        gitRepoLink: record.sus_git_repo,
                        data_account_id: record.data_account_id,
                      };
                      setRequestBody(body);
                      setSelectedSpaceGxpFlag(record.gxpFlag);
                      handleDeleteAccess(record.metaDataUsers);
                    }}
                  >
                    <span style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}>
                      <DeleteOutlined style={{ fontSize: 16 }} />
                      Delete Space
                    </span>
                  </Menu.Item>
                </Menu>
              }
              trigger={["click"]}
              open={openDropdownKey === `${record.bucketName}-${record.spaceName}`}
              placement="bottomLeft"
              overlayClassName="action-menu-dropdown"
              onOpenChange={(open) => handleMenuOpen(record, open)}
            >
              <span
                style={{
                  cursor: "pointer",
                  fontSize: 21,
                  color: "#1677ff",
                  padding: "6px 12px",
                  borderRadius: 6,
                  display: "inline-block",
                  transition: "background 0.2s",
                }}
              >
                <MoreOutlined />
              </span>
            </Dropdown>
          );
        }

        // Show full menu for normal/active spaces (with Retry as last item if there's an error)
        const gridExtraMenuItems = (
          <>
            <Menu.Item
              key="parametersSecrets"
              style={{ color: "#1677ff", fontWeight: 500, fontSize: 15, paddingLeft: 16 }}
              onClick={(e) => {
                e.domEvent.stopPropagation();
                setOpenDropdownKey(null);
                handleOpenParametersSecretsModal(record);
              }}
            >
              <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <SettingOutlined style={{ fontSize: 16 }} />
                View/Edit Parameters &amp; Secrets
              </span>
            </Menu.Item>

            <Menu.Item
              key="viewMetadata"
              disabled={!record.sus_project_metadata}
              style={{
                color: record.sus_project_metadata ? "#1677ff" : "#bfbfbf",
                fontWeight: 500,
                fontSize: 15,
                paddingLeft: 16,
              }}
              onClick={async (e) => {
                e.domEvent.stopPropagation();
                setOpenDropdownKey(null);
                const key = "MessageKey";
                message.open({ key, type: "info", content: "Fetching Metadata Form...", duration: 8 });
                const sagemaker_id = getSagemakerDomainByDisplayName("Common");
                if (record.sus_project_metadata && sagemaker_id) {
                  const readOnlyAssetID = await getReadOnlyDzLink(msalInstance, record.spaceName);
                  if (readOnlyAssetID.success) {
                    const readOnlyLink = `https://${sagemaker_id}.sagemaker.us-east-1.on.aws/browse/assetListing/${readOnlyAssetID.data}/latest/metadata`;
                    message.open({ key, type: "success", content: "Bussiness Metadata form Loaded Successfully", duration: 2 });
                    window.open(readOnlyLink, "_blank", "noopener,noreferrer");
                  } else {
                    message.open({ key, type: "error", content: "Error fetching Business Metadata form", duration: 2 });
                  }
                }
              }}
            >
              <Tooltip
                title={!record.sus_project_metadata ? "Business metadata not available for this asset" : ""}
                placement="left"
              >
                <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
                  <EyeOutlined style={{ fontSize: 16 }} />
                  View Business Metadata
                </span>
              </Tooltip>
            </Menu.Item>

            <SubscribeMenuItem
              record={record}
              domainName={domainName}
              isOpen={openDropdownKey === `${record.bucketName}-${record.spaceName}`}
              onClose={() => setOpenDropdownKey(null)}
            />

            {hasError && (
              <Menu.Item
                key="retrySpace"
                style={{ color: "#fa541c", fontWeight: 600, fontSize: 15, paddingLeft: 16 }}
                onClick={(e) => {
                  e.domEvent.stopPropagation();
                  setOpenDropdownKey(null);
                  handleRetry(record);
                }}
              >
                <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <SyncOutlined style={{ fontSize: 16 }} />
                  Retry
                </span>
              </Menu.Item>
            )}
          </>
        );

        return (
          <SpaceActionMenu
            record={record}
            domainName={domainName}
            trigger={["click"]}
            isOpen={openDropdownKey === `${record.bucketName}-${record.spaceName}`}
            onOpenChange={(open) => handleMenuOpen(record, open)}
            onRefreshSpaces={onRefreshSpaces}
            onEditClick={(rec) => {
              setOpenDropdownKey(null);
              setEditingRecord(rec as any);
            }}
            onDeleteClick={(rec) => {
              setOpenDropdownKey(null);
              const body: IrequestBody = {
                region: rec.regionName,
                data_domain_name: domainName,
                bucket_name: rec.bucketName,
                space_name: rec.spaceName,
                data_account_id: rec.data_account_id,
                gitRepoLink: (rec as any).sus_git_repo,
              };
              setRequestBody(body);
              setSelectedSpaceGxpFlag(rec.gxpFlag);
              handleDeleteAccess((rec as any).metaDataUsers);
            }}
            extraMenuItems={gridExtraMenuItems}
          />
        );

        // ─── old inline menu removed — now handled by SpaceActionMenu above ─
        if (false) { 
          const _removed = (
          <Dropdown
            overlay={
              <Menu className="action-menu-custom">
                <Menu.Item
                  key="copyS3"
                  style={{ color: "#1677ff", fontWeight: 500, fontSize: 15, paddingLeft: 16 }}
                  onClick={async (e) => {
                    e.domEvent.stopPropagation();
                    setOpenDropdownKey(null);
                    try {
                      await navigator.clipboard.writeText(`s3://${record.bucketName}`);
                      message.success("Copied S3 URI to clipboard!");
                    } catch {
                      message.error("Failed to copy URI");
                    }
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <LinkOutlined style={{ fontSize: 16 }} />
                    Copy S3 URI
                  </span>
                </Menu.Item>

                <Menu.Item
                  key="copySpaceUri"
                  style={{ color: "#1677ff", fontWeight: 500, fontSize: 15, paddingLeft: 16 }}
                  onClick={async (e) => {
                    e.domEvent.stopPropagation();
                    setOpenDropdownKey(null);
                    try {
                      const preferredSpaceName = record.spaceNameMasked || record.spaceName;
                      const spaceURI = `${window.location.origin}/explorer/${encodeURIComponent(domainName)}/${encodeURIComponent(preferredSpaceName)}`;
                      await navigator.clipboard.writeText(spaceURI);
                      message.success("Copied Space URI to clipboard!");
                    } catch {
                      message.error("Failed to copy URI");
                    }
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <CopyOutlined style={{ fontSize: 16 }} />
                    Copy Space URI
                  </span>
                </Menu.Item>

                <Menu.Item
                  key="editSpace"
                  style={{
                    color: "#1677ff",
                    fontWeight: 500,
                    fontSize: 15,
                    paddingLeft: 16,
                  }}
                  onClick={(e) => {
                    e.domEvent.stopPropagation();
                    setOpenDropdownKey(null);
                    setEditingRecord(record);
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <EditOutlined style={{ fontSize: 16 }} />
                    View/Edit Space Metadata
                  </span>
                </Menu.Item>

                <Menu.Item
                  key="parametersSecrets"
                  style={{ color: "#1677ff", fontWeight: 500, fontSize: 15, paddingLeft: 16 }}
                  onClick={(e) => {
                    e.domEvent.stopPropagation();
                    setOpenDropdownKey(null);
                    handleOpenParametersSecretsModal(record);
                  }}
                >
                  <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
                    <SettingOutlined style={{ fontSize: 16 }} />
                    View/Edit Parameters & Secrets
                  </span>
                </Menu.Item>

                <Menu.Item
                  key="viewMetadata"
                  disabled={!record.sus_project_metadata}
                  style={{
                    color: record.sus_project_metadata ? "#1677ff" : "#bfbfbf",
                    fontWeight: 500,
                    fontSize: 15,
                    paddingLeft: 16,
                  }}
                  onClick={ async (e) => {
					e.domEvent.stopPropagation();
					setOpenDropdownKey(null);
					const key = "MessageKey";
					message.open({
						key,
						type: "info",
						content: "Fetching Metadata Form...",
						duration: 8,
					});
					const sagemaker_id = getSagemakerDomainByDisplayName("Common");
					if (record.sus_project_metadata && sagemaker_id) {
						const readOnlyAssetID = await getReadOnlyDzLink(msalInstance, record.spaceName);
						if (readOnlyAssetID.success) {
							const readOnlyLink = `https://${sagemaker_id}.sagemaker.us-east-1.on.aws/browse/assetListing/${readOnlyAssetID.data}/latest/metadata`;
							console.log("READONLYLINK", readOnlyLink);
							message.open({
								key,
								type: "success",
								content: "Bussiness Metadata form Loaded Successfully",
								duration: 2,
							});
							window.open(readOnlyLink, "_blank", "noopener,noreferrer");
						} else {
							message.open({
								key,
								type: "error",
								content: "Error fetching Business Metadata form",
								duration: 2,
							});
						}
					}
                  }}
                >
                  <Tooltip
                    title={!record.sus_project_metadata ? "Business metadata not available for this asset" : ""}
                    placement="left"
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
                      <EyeOutlined style={{ fontSize: 16 }} />
                      View Business Metadata
                    </span>
                  </Tooltip>
                </Menu.Item>

                <SubscribeMenuItem
                  record={record}
                  domainName={domainName}
                  isOpen={openDropdownKey === `${record.bucketName}-${record.spaceName}`}
                  onClose={() => setOpenDropdownKey(null)}
                />

                <Menu.Item
                  key="deleteSpace"
                  style={{ color: "#ff4d4f", fontWeight: 500, fontSize: 15, paddingLeft: 16 }}
                  onClick={() => {
                    setOpenDropdownKey(null);
                    const body: IrequestBody = {
                      region: record.regionName,
                      data_domain_name: domainName,
                      bucket_name: record.bucketName,
                      space_name: record.spaceName,
                      data_account_id: record.data_account_id,
					            gitRepoLink: record.sus_git_repo,
                    };

                    setRequestBody(body);
                    setSelectedSpaceGxpFlag(record.gxpFlag);
                    handleDeleteAccess(record.metaDataUsers);
                  }}
                >
                  <span style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                  }}>
                    <DeleteOutlined style={{ fontSize: 16 }} />
                    Delete Space
                  </span>
                </Menu.Item>

                {hasError && (
                  <Menu.Item
                    key="retrySpace"
                    style={{ color: "#fa541c", fontWeight: 600, fontSize: 15, paddingLeft: 16 }}
                    onClick={(e) => {
                      e.domEvent.stopPropagation();
                      setOpenDropdownKey(null);
                      handleRetry(record);
                    }}
                  >
                    <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <SyncOutlined style={{ fontSize: 16 }} />
                      Retry
                    </span>
                  </Menu.Item>
                )}
              </Menu>
            }
            trigger={["click"]}
            open={openDropdownKey === `${record.bucketName}-${record.spaceName}`}
            placement="bottomLeft"
            overlayClassName="action-menu-dropdown"
            onOpenChange={(open) => handleMenuOpen(record, open)}
          >
            <span
              style={{
                cursor: "pointer",
                fontSize: 21,
                color: "#1677ff",
                padding: "6px 12px",
                borderRadius: 6,
                display: "inline-block",
                transition: "background 0.2s",
              }}
            >
              <MoreOutlined />
            </span>
          </Dropdown>
        ); 
      } // end if (false) — dead code block
      },

    },
  ];

  const effectiveTotal = pagination.total;
  

  return (
    <div className="space-wrapper" ref={collapseRef}>
      {/* Edit metadata drawer — lives outside the dropdown so it is never unmounted by dropdown closing */}
      {editingRecord && (
        <AddOrEditSpace
          mode="edit"
          autoTrigger
          editPayload={{
            bucketName: editingRecord.spaceName,
            spaceName: editingRecord.spaceName,
            region: editingRecord.regionName,
            domainName: domainName,
            actualBucketName: editingRecord.bucketName,
          }}
          onSuccess={onRefreshSpaces}
          onDrawerClose={() => setEditingRecord(null)}
          domainName={domainName}
        />
      )}
      <Collapse
        activeKey={activeKeys}
        onChange={(keys) => {
          const activeEl = document.activeElement;

          if (
            activeEl?.tagName === "INPUT" &&
            collapseRef.current?.contains(activeEl)
          ) {
            return;
          }
          setActiveKeys(keys as string[]);
        }}
        style={{ backgroundColor: "#f5f5f6" }}>

        <Panel
          key="1"
          header={
            <div className="space-main-header">
              <div className="space-panel-header"
                onKeyDown={e => {
                  if (e.key === "Enter") e.stopPropagation();
                }}
                onKeyUp={e => {
                  if (e.key === "Enter") e.stopPropagation();
                }}
                tabIndex={-1}>
                <span>
                  List of Spaces under {regionName} region of{" "}
                  {domainName} domain
                </span>
                <div
                  style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}
                  onClick={(e) => {
                    e.stopPropagation();
                  }}

                  onMouseDown={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                >
                  <Input.Search
                    placeholder="Search spaces"
                    allowClear
                    value={searchTerm}
                    onChange={(e) => handleSearchTermChange(e.target.value)}

                    onPressEnter={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}

                    onKeyUp={(e) => {
                      e.stopPropagation();
                    }}
                    style={{ maxWidth: 200 }}
                  />
                  <Button
                    size="middle"
                    className="space-header-btn"
                    onClick={handleSortByName}
                  >
                    Sort by Name ({getCurrentSortOrder() === "asc" ? "A → Z" : "Z → A"})
                  </Button>
                  <Button
                    size="middle"
                    className="space-header-btn"
                    onClick={handleFilterModalOpen}
                  >
                    <FilterOutlined />
                  </Button>
                  <Tooltip title="Refresh">
                    <Button
                      size="middle"
                      className="space-header-btn"
                      icon={<SyncOutlined />}
                      onClick={handleRefresh}
                    />
                  </Tooltip>
                  <Dropdown
                    overlay={
                      <Menu className="settings-menu" style={{ padding: "8px", minWidth: "200px" }}>
                        <Menu.Item key="health-status" style={{ padding: "8px 12px" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <span>Health Status</span>
                            <Switch
                              checked={showHealthStatus}
                              onChange={setShowHealthStatus}
                              size="small"
                            />
                          </div>
                        </Menu.Item>
                      </Menu>
                    }
                    trigger={["click"]}
                    placement="bottomRight"
                  >
                    <Tooltip title="Settings">
                      <Button
                        size="middle"
                        className="space-header-btn"
                        icon={<SettingOutlined />}
                      />
                    </Tooltip>
                  </Dropdown>
                  <FilterModal
                    visible={isFilterModalVisible}
                   onClose={() => setIsFilterModalVisible(false)}
                    onApply={handleFilterApply}
                    resetSignal={filterResetSignal}
                    selectedProfileTypes={selectedProfileTypes}
                    setSelectedProfileTypes={setSelectedProfileTypes}
                  />
                </div>
              </div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: "8px", marginBottom: "10px" }}>
                {Object.entries(appliedFilters)
                  .filter(([_, value]) => value !== undefined && value !== null && value !== "" && (!Array.isArray(value) || value.length > 0))
                  .map(([key, value]) => (
                    <Tag key={key} style={{ cursor: "text", backgroundColor: "#f0f0f0", color: "#1890ff" }} onClick={(e) => e.stopPropagation() }>
                      {FILTER_KEY_LABELS[key] ?? key}: {Array.isArray(value) ? value.map((v: string) => FILTER_VALUE_LABELS[v] ?? v).join(", ") : (FILTER_VALUE_LABELS[String(value)] ?? value)}
                    </Tag>
                  ))}
              </div>
            </div>

          }
        >
          <Box className="space-table-container">
            {isSpaceListFetching ? (
              <FormSkeleton layout={formSkeleton} />
            ) : allRows.length === 0 ? (
              <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 3 }}>
                <Grid item xs={12}>
                  <Empty description="No Spaces found" />
                </Grid>
              </Grid>
            ) : (
              <Table
                dataSource={allRows.map((row, idx) => ({
                  ...row,
                  key: `${row.spaceName}-${idx}`,
                  __onClick__: viewObjects,
                }))}
                columns={columns}
                pagination={{
                  current: pagination.current,
                  pageSize: pagination.pageSize,
                  total: effectiveTotal,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} spaces`,
                  onChange: onPaginationChange,
                  onShowSizeChange: onPaginationChange,
                  showSizeChanger: true,
                  pageSizeOptions: ["10", "20", "50"],
                }}
                onChange={handleTableChange}
                rowClassName={(record) => {
                  // Only apply disabled style if in-progress WITHOUT error
                  const hasError = hasCreateError(record);
                  const isInProgress = record.is_active === -1;
                  return (isInProgress && !hasError) ? "space-row-disabled" : "";
                }}

                onRow={(record) => {
                  // Only handle in-progress rows, not error rows
                  if (record.is_active === -1 && !hasCreateError(record)) {
                    return {
                      onMouseEnter: () => setHoveredRowKey(record.spaceName),
                      onMouseLeave: () => setHoveredRowKey(null),
                    };
                  }
                  return {};
                }}

                components={{
                  body: {
                    row: (props: any) => {
                      const { className, ...restProps } = props;
                      const currentPageRows = allRows;
                      const recordKey = restProps["data-row-key"];
                      let record = null;

                      if (recordKey) {
                        const parts = recordKey.split("-");
                        const rowIndex = parseInt(parts[parts.length - 1]);
                        if (!isNaN(rowIndex) && currentPageRows[rowIndex]) record = currentPageRows[rowIndex];
                      }

                      const isError = record ? hasCreateError(record) : false;
                      const isInProgress = record?.is_active === -1;

                      // Only show overlay for in-progress, not for error rows
                      if (record && isInProgress && !isError) {
                        const isHovered = hoveredRowKey === record.spaceName;

                        return (
                          <tr
                            {...restProps}
                            className={className ? `${className} space-row-disabled` : "space-row-disabled"}
                            style={{ position: "relative", cursor: "not-allowed" }}
                          >
                            {restProps.children}
                            {isHovered && (
                              <>
                                <div
                                  style={{
                                    position: "absolute",
                                    inset: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    background: "rgba(255,255,255,.6)",
                                    pointerEvents: "none",
                                    zIndex: 2,
                                  }}
                                >
                                  <div className="dots-loading">
                                    <div className="dot" />
                                    <div className="dot" />
                                    <div className="dot" />
                                    <div className="dot" />
                                  </div>
                                </div>

                                <div
                                  className="space-row-tooltip"
                                  style={{
                                    position: "absolute",
                                    bottom: "100%",
                                    transform: "translateY(-6px)",
                                    padding: "8px 10px",
                                    background: "#1f1f1f",
                                    color: "#fff",
                                    borderRadius: 6,
                                    maxWidth: 420,
                                    lineHeight: 1.3,
                                    pointerEvents: "none",
                                    boxShadow: "0 6px 16px rgba(0,0,0,.2)",
                                    zIndex: 3,
                                    whiteSpace: "normal",
                                  }}
                                >
                                  Space creation is in process.
                                </div>
                              </>
                            )}
                          </tr>
                        );
                      }

                      return <tr {...restProps} className={className} />;
                    },
                  },
                }}
              />
            )}
          </Box>
        </Panel>
      </Collapse>
      <ConfirmDeleteModal
        visible={isDeleteModalVisible}
        onConfirm={handleConfirm}
        onCancel={handleCancel}
        spaceType={selectedSpaceGxpFlag}
        gitRepoLink={requestBody?.gitRepoLink}
      />
      <ErrorModal
        visible={isErrorModalVisible}
        title="Space Deletion Restricted"
        message={errorModalMessage}
        type="restriction"
        onClose={() => setIsErrorModalVisible(false)}
      />

      {/* Parameters & Secrets Modal */}
      <Modal
        title="Parameters and Secrets"
        open={parametersSecretsModalVisible}
        onCancel={() => {
          if (isSavingParameters) return;
          // Reset to default empty rows
          setParameters([{ name: "", value: "" }]);
          setSecrets([{ name: "", value: "" }]);
          setDeletedParameters([]);
          setDeletedSecrets([]);
          setParametersSecretsModalVisible(false);
        }}
        maskClosable={!isSavingParameters}
        closable={!isSavingParameters}
        width={900}
        centered
        bodyStyle={{
          height: 500,
          padding: "20px",
          overflow: "hidden",
        }}
        footer={[
          <Button
            key="cancel"
            disabled={isSavingParameters}
            onClick={() => {
              // Reset to default empty rows
              setParameters([{ name: "", value: "" }]);
              setSecrets([{ name: "", value: "" }]);
              setDeletedParameters([]);
              setDeletedSecrets([]);
              setParametersSecretsModalVisible(false);
            }}
          >
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={isSavingParameters}
            disabled={loadingParameters || isSavingParameters || !hasChanges}
            onClick={handleSaveParametersSecrets}
          >
            {isSavingParameters ? "Updating..." : "Save Changes"}
          </Button>,
        ]}
      >
        <Spin spinning={loadingParameters} tip="Loading parameters and secrets...">
          <div style={{ height: "100%", display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Parameters Table */}
            <div style={{ flex: 1, minHeight: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <Text strong style={{ fontSize: 16 }}>Parameters</Text>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setParameters([...parameters, { name: "", value: "" }])}
                >
                  Add
                </Button>
              </div>

              <div style={{ height: "calc(100% - 48px)", border: "1px solid #d9d9d9", borderRadius: 6, overflow: "auto" }}>
                <Table
                  dataSource={loadingParameters ? [] : parameters.map((param, index) => ({ ...param, key: index }))}
                  pagination={false}
                  size="small"
                  scroll={{ y: 200, scrollToFirstRowOnChange: false }}
                  style={{ height: "100%" }}
                  locale={{
                    emptyText: loadingParameters
                      ? "Loading..."
                      : "No parameters added. Click 'Add' to create one.",
                  }}
                  columns={[
                    {
                      title: "Name",
                      dataIndex: "name",
                      key: "name",
                      width: "30%",
                      render: (text, record, index) => (
                        <Input
                          value={text}
                          placeholder="Parameter name"
                          onChange={(e) => {
                            const updated = [...parameters];
                            updated[index].name = e.target.value;
                            setParameters(updated);
                          }}
                        />
                      ),
                    },
                    {
                      title: "Value",
                      dataIndex: "value",
                      key: "value",
                      width: "30%",
                      render: (text, record, index) => (
                        <Input
                          value={text || ""}
                          placeholder="Parameter value"
                          onChange={(e) => {
                            const updated = [...parameters];
                            updated[index].value = e.target.value;
                            setParameters(updated);
                          }}
                        />
                      ),
                    },
                    {
                      title: "Path",
                      dataIndex: "path",
                      key: "path",
                      width: "25%",
                      render: (text, record, index) => (
                        <Input
                          value={text || ""}
                          readOnly
                          placeholder="N/A"
                          addonAfter={
                            <Button
                              type="text"
                              icon={<CopyOutlined />}
                              size="small"
                              style={{ border: "none", color: "#1677ff" }}
                              onClick={async () => {
                                if (text) {
                                  try {
                                    await navigator.clipboard.writeText(text);
                                    message.success("Path copied to clipboard!");
                                  } catch {
                                    message.error("Failed to copy path");
                                  }
                                } else {
                                  message.warning("No path to copy");
                                }
                              }}
                            />
                          }
                        />
                      ),
                    },
                    {
                      title: "Actions",
                      key: "actions",
                      width: "15%",
                      render: (_, record, index) => (
                        <Button
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => {
                            if (parameters.length === 1) {
                              message.warning("At least one parameter is required");
                              return;
                            }
                            Modal.confirm({
                              title: "Delete Parameter",
                              content: `Are you sure you want to delete the parameter "${parameters[index].name}"?`,
                              okText: "Delete",
                              cancelText: "Cancel",
                              okButtonProps: { danger: true },
                              onOk: () => {
                                const deleted = parameters[index];
                                if (originalParameters.some(p => p.name === deleted.name && p.name.trim())) {
                                  setDeletedParameters(prev => [...prev, deleted.name]);
                                }
                                setParameters(parameters.filter((_, i) => i !== index));
                                message.success("Parameter deleted");
                              },
                            });
                          }}
                        />
                      ),
                    },
                  ]}
                />
              </div>
            </div>

            {/* Secrets Table */}
            <div style={{ flex: 1, minHeight: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
                <Text strong style={{ fontSize: 16 }}>Secrets</Text>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => setSecrets([...secrets, { name: "", value: "" }])}
                >
                  Add
                </Button>
              </div>
              
              <div style={{ height: "calc(100% - 48px)", border: "1px solid #d9d9d9", borderRadius: 6, overflow: "auto" }}>
                <Table
                  dataSource={loadingParameters ? [] : secrets.map((secret, index) => ({ ...secret, key: index }))}
                  pagination={false}
                  size="small"
                  scroll={{ y: 200, scrollToFirstRowOnChange: false }}
                  style={{ height: "100%" }}
                  locale={{
                    emptyText: loadingParameters
                      ? "Loading..."
                      : "No secrets added. Click 'Add' to create one.",
                  }}
                  columns={[
                    {
                      title: "Name",
                      dataIndex: "name",
                      key: "name",
                      width: "30%",
                      render: (text, record, index) => (
                        <Input
                          value={text}
                          placeholder="Secret name"
                          onChange={(e) => {
                            const updated = [...secrets];
                            updated[index].name = e.target.value;
                            setSecrets(updated);
                          }}
                        />
                      ),
                    },
                    {
                      title: "Value",
                      dataIndex: "value",
                      key: "value",
                      width: "30%",
                      render: (text, record, index) => (
                        <Input.Password
                          value={text || ""}
                          placeholder="Secret value"
                          onChange={(e) => {
                            const updated = [...secrets];
                            updated[index].value = e.target.value;
                            setSecrets(updated);
                          }}
                        />
                      ),
                    },
                    {
                      title: "ARN",
                      dataIndex: "arn",
                      key: "arn",
                      width: "25%",
                      render: (text, record, index) => (
                        <Input
                          value={text || ""}
                          readOnly
                          placeholder="N/A"
                          addonAfter={
                            <Button
                              type="text"
                              icon={<CopyOutlined />}
                              size="small"
                              style={{ border: "none", color: "#1677ff" }}
                              onClick={async () => {
                                if (text) {
                                  try {
                                    await navigator.clipboard.writeText(text);
                                    message.success("ARN copied to clipboard!");
                                  } catch {
                                    message.error("Failed to copy ARN");
                                  }
                                } else {
                                  message.warning("No ARN to copy");
                                }
                              }}
                            />
                          }
                        />
                      ),
                    },
                    {
                      title: "Actions",
                      key: "actions",
                      width: "15%",
                      render: (_, record, index) => (
                        <Button
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => {
                            if (secrets.length === 1) {
                              message.warning("At least one secret is required");
                              return;
                            }
                            Modal.confirm({
                              title: "Delete Secret",
                              content: `Are you sure you want to delete the secret "${secrets[index].name}"?`,
                              okText: "Delete",
                              cancelText: "Cancel",
                              okButtonProps: { danger: true },
                              onOk: () => {
                                const deleted = secrets[index];
                                if (originalSecrets.some(s => s.name === deleted.name && s.name.trim())) {
                                  setDeletedSecrets(prev => [...prev, deleted.name]);
                                }
                                setSecrets(secrets.filter((_, i) => i !== index));
                                message.success("Secret deleted");
                              },
                            });
                          }}
                        />
                      ),
                    },
                  ]}
                />
              </div>
            </div>
          </div>
        </Spin>
      </Modal>
    </div>
  );
};

export default SpaceFolderGrid;
